import React, { Fragment, useEffect } from 'react';

// @ts-ignore TODO: fix noImplicitAny error here
import Helmet from 'react-helmet';
import { InjectedIntl, injectIntl } from 'react-intl';

import { Grid, GridColumn } from '@atlaskit/page';
import PageHeader from '@atlaskit/page-header';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import Loading from 'src/components/loading';
import { Workspace } from 'src/components/types';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { AppInstalledStatus, EmptyStateKind } from 'src/redux/jira/constants';
import { WorkspacePermission } from 'src/redux/workspaces/constants';
import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';
import CreateBranchDialog from 'src/sections/create-branch/containers/dialog';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { publishScreenEvent } from 'src/utils/analytics/publish';

import { CreateJiraIssueInlineDialog } from './../create-jira-issue/create-jira-issue-inline-dialog';
import { SCREEN_NAME } from './constants';
import EmptyStateConnect from './empty-state-connect';
import EmptyStateTry from './empty-state-try';
import { ErrorState } from './error-state';
import { ManageProjectsDialog } from './manage-projects';
import messages from './page.i18n';
import * as styles from './page.styled';
import { JiraPageM2 } from './relevant-issues/relevant-issues-content';
import { useSyncFilterStateWithUrl } from './relevant-issues/use-sync-filter-state-with-url';
import ProjectMenu from './relevant-projects-menu/project-menu';

type JiraPageDispatchProps = {
  initJiraRepoPage: (repositoryFullSlug: string) => void;
  installJiraAddon: () => void;
  hydrateJiraData: (repositoryFullSlug: string) => void;
  resetState: () => void;
};

type JiraPageStateProps = {
  appInstalledStatus: AppInstalledStatus;
  repositoryFullSlug: string;
  sitesFetchedStatus: LoadingStatus;
  relevantSitesFetchedStatus: LoadingStatus;
  workspace: Workspace;
  workspaceName: string;
  workspacePermission?: WorkspacePermission;
  workspacePermissionFetchedStatus: LoadingStatus;
  emptyStateKind: EmptyStateKind;
  intl: InjectedIntl;
  isUpToSmallBreakpointActive: boolean;
};

type JiraPageProps = JiraPageDispatchProps &
  JiraPageStateProps &
  RouteComponentProps;

const JiraPage = injectIntl((props: JiraPageProps) => {
  const {
    appInstalledStatus,
    emptyStateKind,
    initJiraRepoPage,
    hydrateJiraData,
    installJiraAddon,
    repositoryFullSlug,
    resetState,
    sitesFetchedStatus,
    relevantSitesFetchedStatus,
    workspacePermission,
    workspacePermissionFetchedStatus,
    location,
    history,
    intl,
    isUpToSmallBreakpointActive,
  } = props;

  const hasError =
    appInstalledStatus === AppInstalledStatus.FailedToFetch ||
    appInstalledStatus === AppInstalledStatus.FailedToInstall ||
    sitesFetchedStatus === LoadingStatus.Failed ||
    relevantSitesFetchedStatus === LoadingStatus.Failed;

  useSyncFilterStateWithUrl(location, history);
  const PAGE_TITLE = intl.formatMessage(messages.jiraIssues);

  useEffect(() => {
    return () => {
      resetState();
    };
    // This hook is meant to be run only once, hence the empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (repositoryFullSlug) {
      hydrateJiraData(repositoryFullSlug);
    }
  }, [repositoryFullSlug, hydrateJiraData]);

  useEffect(() => {
    if (appInstalledStatus === AppInstalledStatus.NotInstalled) {
      installJiraAddon();
    }
  }, [appInstalledStatus, installJiraAddon]);

  const isPageLoadingComplete =
    [
      AppInstalledStatus.FailedToInstall,
      AppInstalledStatus.FailedToFetch,
    ].includes(appInstalledStatus) ||
    [relevantSitesFetchedStatus, sitesFetchedStatus].includes(
      LoadingStatus.Failed
    ) ||
    emptyStateKind !== EmptyStateKind.Before;

  useEffect(() => {
    if (
      repositoryFullSlug &&
      appInstalledStatus === AppInstalledStatus.Installed
    ) {
      initJiraRepoPage(repositoryFullSlug);
    }
  }, [appInstalledStatus, initJiraRepoPage, repositoryFullSlug]);

  useEffect(() => {
    // Users might have moved to a different tab to either:
    // 1. Sign up for a new Jira site by clicking on the "Try Jira" link
    // 2. Opened settings page to connect to an existing site
    // In either case, fetch the sites again when the tab regains visibility
    // to display the updated state.
    const onVisibilityChange = () => {
      if (
        appInstalledStatus === AppInstalledStatus.Installed &&
        (emptyStateKind === EmptyStateKind.HasAvailableSites ||
          emptyStateKind === EmptyStateKind.HasNoAvailableSites)
      ) {
        if (repositoryFullSlug && document.visibilityState === 'visible') {
          initJiraRepoPage(repositoryFullSlug);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    appInstalledStatus,
    initJiraRepoPage,
    repositoryFullSlug,
    emptyStateKind,
  ]);

  useEffect(() => {
    if (hasError) {
      publishScreenEvent(SCREEN_NAME, { variation: 'error' });
    }
  }, [hasError]);

  // Do not show project navigation when no sites connected
  const showProjectNavigation = ![
    EmptyStateKind.HasAvailableSites,
    EmptyStateKind.HasNoAvailableSites,
  ].includes(emptyStateKind);

  const createIssueButtonEnabled = emptyStateKind === EmptyStateKind.None;

  const renderHeader = () => (
    <PageHeader
      actions={
        <styles.PageHeaderActionsWrapper>
          <styles.PageHeaderActionItem data-testid="jira-tab-create-jira-issue-button">
            <CreateJiraIssueInlineDialog
              isDisabled={!createIssueButtonEnabled}
            />
          </styles.PageHeaderActionItem>
          {showProjectNavigation && (
            <styles.PageHeaderActionItem>
              <ProjectMenu />
            </styles.PageHeaderActionItem>
          )}
        </styles.PageHeaderActionsWrapper>
      }
      breadcrumbs={<Breadcrumbs />}
    >
      <styles.PageTitleWrapper>{PAGE_TITLE}</styles.PageTitleWrapper>
    </PageHeader>
  );

  // eslint-disable-next-line consistent-return
  const renderPageBody = () => {
    if (hasError) {
      return (
        <React.Fragment>
          {renderHeader()}
          <styles.ErrorStateWrapper>
            <ErrorState
              message={messages.errorPageMessage}
              onRetry={() => {
                if (appInstalledStatus === AppInstalledStatus.Installed) {
                  initJiraRepoPage(repositoryFullSlug);
                } else {
                  installJiraAddon();
                }
              }}
            />
          </styles.ErrorStateWrapper>
        </React.Fragment>
      );
    }
    if (!isPageLoadingComplete) {
      return (
        <styles.LoadingWrapper>
          <Loading size="large" />
        </styles.LoadingWrapper>
      );
    } else if (
      ![
        EmptyStateKind.HasAvailableSites,
        EmptyStateKind.HasNoAvailableSites,
      ].includes(emptyStateKind)
    ) {
      return (
        <Fragment>
          {renderHeader()}
          <JiraPageM2 emptyState={emptyStateKind} />
        </Fragment>
      );
    } else if (emptyStateKind === EmptyStateKind.HasAvailableSites) {
      return (
        <Fragment>
          {renderHeader()}
          <EmptyStateConnect
            workspace={props.workspace}
            workspaceName={props.workspaceName}
            workspacePermission={props.workspacePermission}
            workspacePermissionFetchedStatus={
              props.workspacePermissionFetchedStatus
            }
          />
        </Fragment>
      );
    } else if (emptyStateKind === EmptyStateKind.HasNoAvailableSites) {
      return (
        <Fragment>
          {renderHeader()}
          <EmptyStateTry
            workspacePermission={workspacePermission}
            workspacePermissionFetchedStatus={workspacePermissionFetchedStatus}
            repositoryFullSlug={repositoryFullSlug}
          />
        </Fragment>
      );
    }
  };

  const tabTitleChangesEnabled = useStatsigGate(
    StatsigFeatureKeys.tabTitleChangesEnabled
  );

  return (
    <>
      <CreateBranchDialog
        requiredLevel="write"
        shouldRenderCreateBranchButton={false}
        source={SCREEN_NAME}
      />
      <ManageProjectsDialog />
      <Grid
        layout="fluid"
        spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
      >
        <Helmet
          defer={false}
          title={tabTitleChangesEnabled ? PAGE_TITLE : `/ ${PAGE_TITLE}`}
        />
        <GridColumn>{renderPageBody()}</GridColumn>
      </Grid>
    </>
  );
});

export default withRouter(JiraPage);
